import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { mensajeWhatsApp, waUrl } from "@/components/GestionarAcceso";
import { EmpresasVisorBtn } from "@/components/VisorEmpresas";
import { CompartirAcceso, VistaPreviaMensajes } from "@/components/CompartirAcceso";
import { ShieldOff, LockOpen } from "lucide-react";
import {
  Users, Plus, Trash2, X, Search, Pencil, Eye, EyeOff, MessageCircle,
  UserCheck, UserX, Copy, RotateCcw,
} from "lucide-react";

const ROLES = [
  { v: "admin", l: "Administrador" },
  { v: "gestor", l: "Gestor" },
  { v: "importador", l: "Importador" },
  { v: "empleado", l: "Empleado" },
  { v: "empresa_cliente", l: "Empresa cliente" },
  { v: "visor", l: "Visor (solo lectura)" },
];

function EmpresaSelect({ value, onChange, testid }) {
  const [empresas, setEmpresas] = useState([]);
  useEffect(() => { api.get("/empresas").then((r) => setEmpresas(r.data)).catch(() => setEmpresas([])); }, []);
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-500">Empresa usuaria *</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
        <option value="">Selecciona la empresa del Directorio</option>
        {empresas.map((e) => <option key={e} value={e}>{e}</option>)}
      </select>
      <p className="mt-1 text-[11px] text-slate-400">Solo verá los empleados de esta empresa, en modo lectura.</p>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-vino-500 focus:ring-2 focus:ring-vino-500/20";

function waLink(telefono, nombre, email, password, role) {
  if (role === "empleado" && password === email) return waUrl(telefono, mensajeWhatsApp(nombre, email));
  const msg = [
    `Hola ${nombre}, te comparto tus credenciales de acceso a Roge by Human RMSA:`,
    "",
    `Correo: ${email}`,
    `Contraseña: ${password}`,
    "",
    `Ingresa en: ${window.location.origin}`,
  ].join("\n");
  return waUrl(telefono, msg);
}

function CredencialesPanel({ nombre, email, password, telefono, role, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(`Correo: ${email}\nContraseña: ${password}`);
    toast.success("Credenciales copiadas");
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md animate-fade-in-up rounded-2xl bg-white p-6 shadow-xl" data-testid="panel-credenciales">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Credenciales de acceso</h3>
          <button onClick={onClose} data-testid="btn-cerrar-credenciales" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <p className="mb-3 text-sm text-slate-500">
          Comparta estas credenciales con <strong className="text-slate-700">{nombre}</strong>. Por seguridad no se volverán a mostrar.
        </p>
        <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
          <p><span className="text-slate-500">Correo:</span> <strong className="text-slate-800">{email}</strong></p>
          <p><span className="text-slate-500">Contraseña:</span> <strong className="text-slate-800">{password}</strong></p>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button onClick={copy} data-testid="btn-copiar-credenciales" className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
            <Copy className="h-4 w-4" /> Copiar
          </button>
          <a
            href={waLink(telefono, nombre, email, password, role)}
            target="_blank"
            rel="noreferrer"
            data-testid="btn-whatsapp-credenciales"
            className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <MessageCircle className="h-4 w-4" /> Enviar por WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

function NuevoUsuarioModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", telefono: "", password: "", role: "empleado" });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/users", form);
      toast.success("Usuario creado");
      setCreated({ ...form, token: r.data.activacion_token });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <CompartirAcceso nombre={created.name} email={created.email} role={created.role} token={created.token}
        telefono={created.telefono} onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md animate-fade-in-up rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Nuevo usuario</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Nombre *</label>
            <input data-testid="usuario-nombre" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Correo *</label>
            <input data-testid="usuario-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Teléfono WhatsApp (opcional)</label>
            <input data-testid="usuario-telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} placeholder="Ej: 3001234567" className={inputCls} />
          </div>
          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">Roge genera un enlace personal de un solo uso (72 h) para que la persona cree su propia contraseña. Nadie más la conoce.</p>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Rol *</label>
            <select data-testid="usuario-rol" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls}>
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          {form.role === "empresa_cliente" && <EmpresaSelect value={form.empresa_cliente} onChange={(v) => set("empresa_cliente", v)} testid="usuario-empresa" />}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="usuario-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
              {saving && <Spinner />} Crear usuario
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditarUsuarioModal({ usuario, esPropia, esAdmin, onClose, onSaved }) {
  const [form, setForm] = useState({ name: usuario.name, email: usuario.email, telefono: usuario.telefono || "", role: usuario.role, password: "", empresa_cliente: usuario.empresa_cliente || "" });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reset, setReset] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, telefono: form.telefono, email: form.email };
      if (!esPropia && esAdmin) payload.role = form.role;
      if (esAdmin && form.role === "empresa_cliente") payload.empresa_cliente = form.empresa_cliente;
      if (form.password) payload.password = form.password;
      await api.patch(`/users/${usuario.id}`, payload);
      toast.success("Usuario actualizado");
      if (form.password) {
        setReset({ nombre: form.name, email: usuario.email, password: form.password, telefono: form.telefono });
      } else {
        onSaved();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (reset) {
    return (
      <CredencialesPanel
        nombre={reset.nombre} email={reset.email} password={reset.password}
        telefono={reset.telefono} onClose={onSaved}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md animate-fade-in-up rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold text-slate-900">Editar usuario</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Nombre *</label>
            <input data-testid="editar-nombre" required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Correo (usuario de acceso) *</label>
            <input data-testid="editar-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Teléfono WhatsApp</label>
            <input data-testid="editar-telefono" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className={inputCls} />
          </div>
          {esAdmin && <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Rol *</label>
            <select data-testid="editar-rol" value={form.role} disabled={esPropia} onChange={(e) => set("role", e.target.value)} className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`}>
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
            {esPropia && <p className="mt-1 text-[11px] text-slate-400">No puede cambiar el rol de su propia cuenta.</p>}
          </div>}
          {esAdmin && form.role === "empresa_cliente" && <EmpresaSelect value={form.empresa_cliente} onChange={(v) => set("empresa_cliente", v)} testid="editar-empresa" />}
          <p className="text-[11px] text-slate-400">Para que cree una nueva contraseña usa el botón de restablecer: Roge generará un enlace de un solo uso.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Cancelar</button>
            <button type="submit" disabled={saving} data-testid="editar-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
              {saving && <Spinner />} Guardar cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Usuarios() {
  const { user } = useAuth();
  const esAdmin = can(user, "usuarios");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [showNuevo, setShowNuevo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [reseteado, setReseteado] = useState(null);

  const loadUsers = () =>
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { loadUsers(); }, []);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q)) return false;
      if (filtroRol && u.role !== filtroRol) return false;
      const activo = u.is_active !== false;
      if (filtroEstado === "activo" && !activo) return false;
      if (filtroEstado === "inactivo" && activo) return false;
      return true;
    });
  }, [users, busqueda, filtroRol, filtroEstado]);

  const toggleEstado = async (u) => {
    const activo = u.is_active !== false;
    const msg = activo
      ? `¿Desactivar a ${u.name}? Podrá entrar y consultar sus datos y documentos, pero no podrá modificar nada.`
      : `¿Reactivar a ${u.name}? Volverá a tener acceso completo según su rol.`;
    if (!window.confirm(msg)) return;
    try {
      await api.patch(`/users/${u.id}/estado`, { is_active: !activo });
      toast.success(activo ? "Cuenta desactivada (solo lectura)" : "Cuenta reactivada");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restablecer = async (u) => {
    if (!window.confirm(`¿Generar un enlace para que ${u.name} cree una nueva contraseña? Su contraseña actual dejará de funcionar y sus sesiones abiertas se cerrarán.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/restablecer-password`);
      toast.success("Enlace de restablecimiento generado");
      setReseteado({ ...r.data, role: u.role });
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const enviarAcceso = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/enviar-acceso`);
      setReseteado({ ...r.data, role: u.role, titulo: "Enviar acceso" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const desbloquear = async (u) => {
    try {
      await api.post(`/users/${u.id}/desbloquear`);
      toast.success(`${u.name} ya puede intentar ingresar de nuevo`);
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restablecerMfa = async (u) => {
    if (!window.confirm(`¿Restablecer la verificación en dos pasos de ${u.name}? Confirma antes su identidad por un canal confiable. Sus sesiones se cerrarán.`)) return;
    try {
      const r = await api.post(`/users/${u.id}/mfa/restablecer`);
      window.prompt(`Código de restablecimiento para ${u.name} (un solo uso, vence en 24 h). Entrégaselo en persona o por llamada; Roge no lo volverá a mostrar:`, r.data.codigo_restablecimiento);
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${u.name}? Su historial laboral, expedientes y contratos se conservan; solo se elimina el acceso.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Cuenta eliminada (historial conservado)");
      loadUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Usuarios y roles</h1>
          <p className="mt-1 text-sm text-slate-500">{esAdmin ? "Gestione las cuentas de acceso, sus roles y su estado." : "Cuentas de acceso de los empleados: datos, contraseña, desbloqueo y envío de acceso."}</p>
        </div>
        {esAdmin && <div className="flex flex-wrap gap-2">
          <VistaPreviaMensajes />
          <button onClick={() => setShowNuevo(true)} data-testid="btn-nuevo-usuario" className="flex items-center gap-2 rounded-lg bg-vino-700 px-3.5 py-2 text-sm font-semibold text-white hover:bg-vino-800">
            <Plus className="h-4 w-4" /> Nuevo usuario
          </button>
        </div>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="filtro-busqueda"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre o correo"
              className={`${inputCls} pl-9`}
            />
          </div>
          {esAdmin && <select data-testid="filtro-rol" value={filtroRol} onChange={(e) => setFiltroRol(e.target.value)} className={`${inputCls} w-auto`}>
            <option value="">Todos los roles</option>
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>}
          <select data-testid="filtro-estado" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className={`${inputCls} w-auto`}>
            <option value="">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Solo lectura</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="tabla-usuarios">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 font-semibold">Nombre</th>
                <th className="px-3 py-2 font-semibold">Correo</th>
                <th className="px-3 py-2 font-semibold">Teléfono</th>
                <th className="px-3 py-2 font-semibold">Rol</th>
                <th className="px-3 py-2 font-semibold">Estado</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400"><Spinner className="mx-auto h-5 w-5" /></td></tr>
              ) : filtrados.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Sin usuarios para los filtros aplicados</td></tr>
              ) : filtrados.map((u) => {
                const activo = u.is_active !== false;
                const esPropia = u.id === user.id;
                return (
                  <tr key={u.id} className="border-b border-slate-100 last:border-0" data-testid={`fila-usuario-${u.id}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-800">
                      {u.name} {esPropia && <span className="ml-1 text-xs text-slate-400">(usted)</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{u.email}</td>
                    <td className="px-3 py-2.5 text-slate-600">{u.telefono || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className="rounded-full bg-vino-50 px-2.5 py-1 text-xs font-medium text-vino-700">{u.role_label}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {activo ? (
                        <span data-testid={`estado-${u.id}`} className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">Activo</span>
                      ) : (
                        <span data-testid={`estado-${u.id}`} className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">Solo lectura</span>
                      )}
                      {u.bloqueado && <span data-testid={`bloqueado-${u.id}`} className="ml-1 rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">Bloqueado</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setEditando(u)} title="Editar" data-testid={`editar-${u.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                          <Pencil className="h-4 w-4" />
                        </button>
                        {!esPropia && (
                          <>
                            <button
                              onClick={() => toggleEstado(u)}
                              title={activo ? "Desactivar (solo lectura)" : "Reactivar"}
                              data-testid={`toggle-estado-${u.id}`}
                              className={`rounded-lg p-2 ${activo ? "text-slate-400 hover:bg-amber-50 hover:text-amber-600" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"}`}
                            >
                              {activo ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </button>
                            {u.bloqueado && <button onClick={() => desbloquear(u)} title="Quitar bloqueo por intentos fallidos" data-testid={`desbloquear-${u.id}`} className="rounded-lg p-2 text-red-600 hover:bg-red-50"><LockOpen className="h-4 w-4" /></button>}
                            {activo && <button onClick={() => enviarAcceso(u)} title="Enviar acceso por WhatsApp" data-testid={`enviar-acceso-${u.id}`} className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"><MessageCircle className="h-4 w-4" /></button>}
                            {esAdmin && <EmpresasVisorBtn u={u} onSaved={loadUsers} />}
                            {esAdmin && u.role === "admin" && u.id !== user?.id && <button onClick={() => restablecerMfa(u)} title="Restablecer verificación en dos pasos" data-testid={`mfa-restablecer-${u.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-amber-50 hover:text-amber-700"><ShieldOff className="h-4 w-4" /></button>}
                            <button onClick={() => restablecer(u)} title="Restablecer contraseña" data-testid={`restablecer-${u.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-vino-50 hover:text-vino-700">
                              <RotateCcw className="h-4 w-4" />
                            </button>
                            {esAdmin && <button onClick={() => removeUser(u)} title="Eliminar cuenta" data-testid={`eliminar-${u.id}`} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600">
                              <Trash2 className="h-4 w-4" />
                            </button>}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400">
          <Users className="h-3.5 w-3.5" /> {filtrados.length} de {users.length} usuarios
        </p>
      </div>

      {reseteado && (
        <CompartirAcceso titulo={reseteado.titulo || "Nueva contraseña: enlace listo"} nombre={reseteado.nombre} email={reseteado.email} token={reseteado.activacion_token} telefono={reseteado.telefono} role={reseteado.role} onClose={() => setReseteado(null)} />
      )}
      {showNuevo && (
        <NuevoUsuarioModal onClose={() => setShowNuevo(false)} onCreated={() => loadUsers()} />
      )}
      {editando && (
        <EditarUsuarioModal
          usuario={editando}
          esPropia={editando.id === user.id}
          esAdmin={esAdmin}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); loadUsers(); }}
        />
      )}
    </div>
  );
}
